/**
 * Cluster 8: Quarterly Market Reports by Council (12 councils)
 * Cluster 9: Zoning Change Alerts by Council (12 councils)
 */

const img = (id) => `https://images.unsplash.com/photo-${id}?w=800&q=85`;

const councils = [
  { name: 'Brisbane City Council', slug: 'brisbane', suburbs: 'Woolloongabba, West End, South Brisbane, Kangaroo Point, Fortitude Valley, New Farm, Teneriffe, Paddington, Milton, Indooroopilly', suburbCount: 31, region: 'Brisbane', medianHouse: '$1.2M–$2.5M', growth: '8–15%', keyProjects: 'Cross River Rail, Brisbane Metro, Gabba Olympic redevelopment, Queens Wharf' },
  { name: 'City of Gold Coast', slug: 'gold-coast', suburbs: 'Surfers Paradise, Broadbeach, Burleigh Heads, Robina, Coomera, Helensvale, Southport, Nerang', suburbCount: 12, region: 'Gold Coast', medianHouse: '$950K–$2.2M', growth: '6–12%', keyProjects: 'Light Rail Stage 4, M1 upgrade, Coomera Town Centre, Southport CBD Priority Development Area' },
  { name: 'Logan City Council', slug: 'logan', suburbs: 'Beenleigh, Logan Central, Springwood, Shailer Park, Marsden, Loganholme', suburbCount: 12, region: 'Logan', medianHouse: '$600K–$850K', growth: '8–14%', keyProjects: 'Logan Motorway upgrade, Meadowbrook health precinct, Logan Metro sports park' },
  { name: 'Moreton Bay Regional Council', slug: 'moreton-bay', suburbs: 'North Lakes, Caboolture, Redcliffe, Petrie, Kallangur, Narangba, Burpengary, Morayfield', suburbCount: 15, region: 'Moreton Bay', medianHouse: '$650K–$950K', growth: '7–13%', keyProjects: 'Petrie University precinct, Redcliffe Peninsula Line, North Lakes expansion, Bruce Highway upgrade' },
  { name: 'Redland City Council', slug: 'redland', suburbs: 'Cleveland, Capalaba, Victoria Point, Thornlands, Wellington Point, Sheldon', suburbCount: 8, region: 'Redlands', medianHouse: '$750K–$1.1M', growth: '6–10%', keyProjects: 'Toondah Harbour, Cleveland CBD renewal, Eastern Transitway' },
  { name: 'Ipswich City Council', slug: 'ipswich', suburbs: 'Ipswich CBD, Springfield, Ripley Valley, Goodna, Redbank Plains, Brassall', suburbCount: 10, region: 'Ipswich', medianHouse: '$550K–$800K', growth: '9–15%', keyProjects: 'Ripley Valley development, Springfield Central expansion, Ipswich CBD transformation, Centenary Highway upgrade' },
  { name: 'Sunshine Coast Council', slug: 'sunshine-coast', suburbs: 'Maroochydore, Caloundra, Kawana, Mooloolaba, Nambour, Buderim', suburbCount: 10, region: 'Sunshine Coast', medianHouse: '$850K–$1.5M', growth: '7–12%', keyProjects: 'Maroochydore CBD development, Sunshine Coast Airport expansion, CAMCOS rail corridor, Caloundra South' },
  { name: 'Toowoomba Regional Council', slug: 'toowoomba', suburbs: 'Toowoomba CBD, Highfields, Westbrook, Rangeville', suburbCount: 5, region: 'Toowoomba', medianHouse: '$500K–$750K', growth: '6–10%', keyProjects: 'Toowoomba Second Range Crossing, Inland Rail hub, Wellcamp Airport precinct' },
  { name: 'Noosa Shire Council', slug: 'noosa', suburbs: 'Noosaville, Noosa Heads, Tewantin, Cooroy, Peregian Springs', suburbCount: 5, region: 'Noosa', medianHouse: '$1.1M–$2.5M', growth: '5–9%', keyProjects: 'Noosa Civic precinct, Tewantin town centre, Peregian Springs expansion' },
  { name: 'Scenic Rim Regional Council', slug: 'scenic-rim', suburbs: 'Beaudesert, Tamborine Mountain, Canungra, Boonah', suburbCount: 4, region: 'Scenic Rim', medianHouse: '$550K–$850K', growth: '7–11%', keyProjects: 'Beaudesert town centre upgrade, Mount Tamborine eco-tourism precinct' },
  { name: 'Lockyer Valley Regional Council', slug: 'lockyer-valley', suburbs: 'Gatton, Laidley, Plainland, Fernvale', suburbCount: 4, region: 'Lockyer Valley', medianHouse: '$450K–$650K', growth: '8–13%', keyProjects: 'Inland Rail, Warrego Highway upgrade, Plainland growth area' },
  { name: 'Somerset Regional Council', slug: 'somerset', suburbs: 'Fernvale, Lowood, Esk, Toogoolawah', suburbCount: 3, region: 'Somerset', medianHouse: '$400K–$600K', growth: '9–14%', keyProjects: 'Fernvale growth corridor, Warrego Highway improvements' },
];

// Cluster 8: Quarterly Market Reports
const createMarketReport = (council, idx) => ({
  id: `${council.slug}-development-site-market-report-2026`,
  category: 'Market Intelligence',
  title: `${council.name} Development Site Market Report 2026`,
  excerpt: `Quarterly analysis of development site activity, pricing trends, and opportunities across ${council.region}. Key suburbs: ${council.suburbs.split(', ').slice(0, 4).join(', ')}.`,
  date: '2026-04-09',
  readTime: '10 min read',
  image: img(idx % 2 === 0 ? '1600585154340-be6161a56a0c' : '1600607687939-ce8a6c25118c'),
  metaDescription: `${council.name} development site market report. Pricing trends, developer activity, and opportunities across ${council.region} suburbs.`,
  suburbs: council.suburbs.split(', '),
  content: `
## ${council.name}: Development Site Market Report

**Report Period:** Q1 2026 (January – March)
**Region:** ${council.region}
**Suburbs Covered:** ${council.suburbs}
**Total Suburbs Monitored:** ${council.suburbCount}

## Market Summary

The ${council.region} development site market continues to demonstrate strong fundamentals heading into 2026. Developer acquisition activity across ${council.name} suburbs has intensified, driven by sustained population growth, housing undersupply, and major infrastructure investment.

Median house prices across the ${council.region} region range from ${council.medianHouse}, with year-on-year growth of ${council.growth} across monitored suburbs. Development site premiums remain elevated, with qualified sites achieving 30–100% above standard residential values.

## Key Market Indicators

### Developer Activity
- Active development applications across ${council.name} remain at elevated levels
- Medium-density townhouse and apartment projects dominate new applications
- Site acquisition enquiries are running above 12-month averages
- Competition for code-assessable sites is particularly intense

### Pricing Trends
Development site pricing across ${council.region} reflects several trends:

| Metric | Current | Previous Quarter | Trend |
|---|---|---|---|
| Avg. site premium over residential | 45–75% | 40–70% | Rising |
| Time on market (dev sites) | 30–60 days | 45–75 days | Shortening |
| Enquiries per listing | 8–15 | 6–12 | Increasing |
| Site consolidation activity | Moderate–High | Moderate | Increasing |

### Supply vs. Demand
- Available development sites in ${council.region} are declining as acquisitions outpace new listings
- Developers are increasingly pursuing off-market acquisitions
- Site consolidation (buying multiple adjoining lots) is becoming more common as standalone sites become scarce

## Major Infrastructure Driving Growth

${council.keyProjects}

These infrastructure investments are:
- Triggering rezoning and planning scheme amendments
- Creating new transit-oriented development precincts
- Increasing end-buyer demand in surrounding suburbs
- Accelerating developer acquisition timelines

## Suburb Spotlight

### Strongest Growth Areas
${council.suburbs.split(', ').slice(0, 3).map(s => `- **${s}** — Strong developer interest, limited remaining sites`).join('\n')}

### Emerging Opportunity Areas
${council.suburbs.split(', ').slice(3, 6).map(s => `- **${s}** — Growing interest as established suburbs become constrained`).join('\n')}

## What This Means for Property Owners

If you own property in a ${council.name} suburb, the current market conditions represent a strong opportunity:

1. **Developer demand exceeds supply** — more buyers than available sites
2. **Premiums are rising** — development sites achieving above-average premiums
3. **Infrastructure investment is peaking** — projects announced but not yet complete create maximum value
4. **Competition is fierce** — well-marketed sites attract multiple qualified offers

> The combination of population growth, housing undersupply, and infrastructure investment makes the current period one of the strongest seller's markets for development sites in ${council.region}'s history.

## Free Market Assessment

ACRES provides complimentary development site assessments for property owners across all ${council.name} suburbs. Our assessment includes current market positioning, zoning analysis, and a realistic value estimate.

**Request your free assessment for any ${council.region} suburb.**

*This report is updated quarterly. Next update: July 2026.*

## Frequently Asked Questions

**Q: How often is this report updated?**

A: We publish quarterly updates in January, April, July, and October, reflecting the most recent market data and trends.

**Q: Is the data specific to development sites or general property?**

A: This report focuses specifically on the development site market — properties with development potential that attract developer buyers. General residential market data is used for context.

**Q: Can I get a suburb-specific report?**

A: Yes. Contact ACRES for a detailed assessment of development site activity and pricing in your specific suburb.
`,
  faq: [
    { q: `How often is the ${council.region} market report updated?`, a: `Quarterly — January, April, July, and October with the most recent market data.` },
    { q: `Can I get a suburb-specific market report?`, a: `Yes. Contact ACRES for a detailed assessment of development site activity in your specific suburb.` },
  ],
});

// Cluster 9: Zoning Change Alerts
const createZoningAlert = (council, idx) => ({
  id: `${council.slug}-zoning-changes-what-they-mean-property`,
  category: 'Market Analysis',
  title: `${council.name} Zoning Changes: What They Mean for Your Property`,
  excerpt: `Recent and upcoming zoning changes under ${council.name} could significantly affect your property's value. Here's what property owners need to know.`,
  date: '2026-04-09',
  readTime: '8 min read',
  image: img(idx % 2 === 0 ? '1564013799-e0cbc9f31999' : '1605146769289-440113cc3d00'),
  metaDescription: `${council.name} zoning changes and planning amendments. How rezoning affects property values in ${council.suburbs.split(', ').slice(0, 4).join(', ')} and surrounding suburbs.`,
  suburbs: council.suburbs.split(', '),
  content: `
## ${council.name} Zoning Changes: What They Mean for Your Property

Zoning changes are one of the most powerful drivers of property value in ${council.region}. When ${council.name} amends its planning scheme to allow higher density or mixed-use development, the affected properties can experience dramatic value increases — often 50–150% above their pre-rezoning value.

Understanding current and upcoming zoning changes helps property owners make informed decisions about their most valuable asset.

## How Zoning Changes Work in ${council.region}

${council.name} periodically reviews and amends its planning scheme to:

- **Respond to population growth** — accommodating more housing in established areas
- **Align with infrastructure investment** — increasing density near new transport
- **Implement state planning policies** — reflecting Queensland Government planning directives
- **Support economic development** — creating employment and mixed-use precincts

### Types of Zoning Changes

| Change Type | Impact | Typical Value Effect |
|---|---|---|
| Upzoning (low → medium density) | Allows townhouses, duplexes | 30–60% value increase |
| Upzoning (medium → high density) | Allows apartments | 50–100% value increase |
| Mixed-use rezoning | Allows commercial + residential | 40–80% value increase |
| Priority Development Area | State-level planning override | 60–150% value increase |
| Overlay removal | Removes development constraints | 20–40% value increase |

## Current Zoning Trends in ${council.region}

### Areas Being Upzoned
${council.name} is progressively increasing development capacity in suburbs near:

- **Transport corridors** — train stations, busways, and major bus routes
- **Activity centres** — shopping centres, employment hubs, and town centres
- **Infrastructure projects** — ${council.keyProjects}

Key suburbs affected: ${council.suburbs}

### What's Driving These Changes

- SEQ Regional Plan requirements for housing density targets
- South East Queensland population forecast: +2 million by 2046
- Housing affordability pressures requiring more diverse housing options
- Olympic infrastructure creating development precincts
- Transit-oriented development policies near new and upgraded stations

## What Rezoning Means for Your Property

If your property is in an area being rezoned or considered for rezoning:

### Before Rezoning
- Your property is valued based on current permitted use
- Development potential is limited to existing zoning
- Standard residential appraisals reflect these limitations

### After Rezoning
- Your property's permitted use expands significantly
- Development potential (and therefore developer interest) increases
- Property value can increase 30–150% depending on the change
- Developer acquisition interest accelerates

### The Window of Opportunity
The greatest value gain occurs in the period immediately **after rezoning is announced but before the market fully adjusts**. Property owners who understand their new zoning rights can capitalise on this window.

## How to Check If Your Property Is Affected

1. **Check the current planning scheme** — available on ${council.name}'s website
2. **Review proposed amendments** — council publishes draft amendments for public consultation
3. **Monitor infrastructure announcements** — new transport often triggers rezoning
4. **Request an ACRES assessment** — we monitor all ${council.name} planning changes and can advise you directly

## Upcoming Changes to Watch

${council.name} has several planning scheme amendments in various stages:

- Transit-oriented development precincts near stations
- Activity centre expansions in key suburbs
- Character area reviews that may unlock development potential
- Infrastructure-triggered rezoning around major projects

> **Not all rezoning benefits are obvious.** Some changes are subtle — a minor amendment to building height limits or setback requirements can significantly increase development feasibility and site value.

## Free Zoning Assessment

ACRES monitors all planning scheme amendments across ${council.name} and advises property owners when changes affect their property. Our complimentary assessment includes:

- Current zoning and overlay analysis
- Identification of proposed changes affecting your property
- Impact assessment on development potential and value
- Recommended actions and timing

**Request your free ${council.region} zoning assessment today.**

## Frequently Asked Questions

**Q: How do I find out if my property's zoning has changed?**

A: Check ${council.name}'s planning scheme online, or contact ACRES for a complimentary review. We monitor all amendments and can advise you directly.

**Q: Can I object to a rezoning that affects my property?**

A: Planning scheme amendments include a public consultation period where submissions can be made. However, if the rezoning increases your property's value, it's typically in your interest.

**Q: Does rezoning happen automatically, or do I need to apply?**

A: Council-initiated rezoning happens through planning scheme amendments. You don't need to apply — the change applies to all properties in the affected area.
`,
  faq: [
    { q: `How do I find out if my ${council.region} property zoning changed?`, a: `Check ${council.name}'s planning scheme online, or contact ACRES for a complimentary review.` },
    { q: `Does rezoning happen automatically?`, a: `Council-initiated rezoning applies to all properties in the affected area automatically through planning scheme amendments.` },
  ],
});

export const quarterlyMarketReports = councils.map((c, i) => createMarketReport(c, i));
export const zoningAlertArticles = councils.map((c, i) => createZoningAlert(c, i));
